import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { mergeAttributes, Node } from '@tiptap/core';
import Suggestion from './suggestionProsePlugin';
import ddt from '@wix/da-ddt';
import { isApiError } from '@wix/da-http-client';
import { requestPuppyFromExtension } from '../utils/requestPuppyFromExtension';
import { toLightweightPapiUserForMentionExtension } from './toLightweightPapiUserForMentionExtension';
import { EXTENSION_TYPE_NAME } from './constants';
import { suggestionExtensionConfig } from './suggestionExtensionConfig';
import { fetchUsersOnPage } from './helpers';
import uniqBy from 'lodash/uniqBy';
import s from '../../components/Editor/styles/base.scss';
async function getItems({ query, editor }) {
    const searched = {
        username: query,
        icon: 'https://a.deviantart.net/avatars/default.gif'
    };
    if (query.length === 0 || query === '') {
        return [];
    }
    const response = await requestPuppyFromExtension(editor, {
        method: 'get',
        url: `/user/friendsforletter`,
        params: {
            letter: query[0],
            search: query,
            page: 0
        }
    }, ()=>{
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line global-require
            const { users } = require('@wix/da-sample-data');
            return {
                users
            };
        }
    });
    if (isApiError(response) || !response) {
        ddt.error('editor-mention', 'Failed to fetch mention suggestions from /user/friendsforletter', response);
        return [
            searched
        ];
    }
    const regexp = new RegExp(`^${query}`, 'i');
    const onServer = response.users;
    const onPage = fetchUsersOnPage();
    const fetched = [
        ...onPage,
        ...onServer !== null && onServer !== void 0 ? onServer : []
    ];
    // filter by search
    // map to correct struct
    const suggestions = fetched.filter((v)=>v.username.match(regexp)).map((v)=>toLightweightPapiUserForMentionExtension(v));
    // filter uniques
    // sort by username
    const sorted = [
        ...new Set(suggestions)
    ].sort((a, b)=>{
        if (a.username === query) {
            return 1;
        }
        if (b.username === query) {
            return -1;
        }
        if (a.username < b.username) {
            return -1;
        }
        if (a.username > b.username) {
            return 1;
        }
        return 0;
    });
    // Use server/page before input
    if (sorted.find((f)=>f.username === searched.username)) {
        return uniqBy([
            ...sorted
        ], 'username');
    }
    return uniqBy([
        ...[
            searched
        ],
        ...sorted
    ], 'username');
}
export const Mention = Node.create({
    name: EXTENSION_TYPE_NAME,
    group: 'inline',
    inline: true,
    selectable: false,
    atom: true,
    addOptions () {
        return {
            HTMLAttributes: {},
            deleteTriggerWithBackspace: false
        };
    },
    addAttributes () {
        return {
            user: {
                default: null,
                parseHTML: (element)=>{
                    try {
                        var _element_getAttribute;
                        return JSON.parse((_element_getAttribute = element.getAttribute('data-user')) !== null && _element_getAttribute !== void 0 ? _element_getAttribute : '');
                    } catch (e) {}
                    return null;
                },
                renderHTML: (attributes)=>{
                    if (!attributes.user) {
                        return {};
                    }
                    return {
                        'data-user': attributes.user
                    };
                }
            }
        };
    },
    addCommands () {
        return {
            daMentionTryMention: (query, range)=>({ commands })=>{
                    if (query.length === 0) {
                        return true;
                    }
                    /*
          // An example of confirming that a user exists before inserting a mention node into the editor.
          //
          // With some additional work, we could notify the user that their mention wasn't made successfully. Or we could,
          // show a little tooltip above the mention with the avatar of the mentioned user for the user's confirmation of
          // mentioning the right one. Idk.
          //
          // Just a proof-of-concept. Should we ever make use of it, we need to update LightweightPapiUserForMentionExtension
          // to require `userId` and `type`

          // The below comment lines would be included before the arrow function definition should we ever use this code:
          // Inside the promise of this function's body, we have to use `this.editor.commands.insertContentAt` instead
          // of the argument version `commands` because it'll be stale and ineffective by the time the promise resolves.

          requestPuppyFromExtension(
            this.editor,
            {
              method: 'get',
              url: `/user/info`,
              params: {
                q: query,
              },
            },
            ():
              | Pick<PapiDaSharedUserInfo, 'user'>
              | { error: string; details: string } => {
              if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line global-require
                const { users } = require('@wix/da-sample-data');
                const user = users.find(
                  user => user.username.toLowerCase() === query.toLowerCase()
                );
                if (user) {
                  return { user };
                }
              }

              return {
                error: 'Internal server error',
                details: 'User not found',
              };
            }
          )
            .then(response => {
              if (isApiError(response) || !response.user) {
                ddt.info(
                  'mention-extension',
                  `No user found with the name ${query}. We will not insert a mention node.`
                );
                return;
              }

              ddt.info(
                'mention-extension',
                `User found for query "${query}". Replacing existing query text with a mention node.`
              );
              this.editor.commands.insertContentAt(
                range,
                {
                  type: this.name,
                  attrs: { user: response.user },
                },
                {
                  // Since this is an asynchronous action, updating the selection would pop the user
                  // away from whatever they're currently typing. Best to leave them where they are.
                  updateSelection: false,
                }
              );
            })
            .catch(e => {
              ddt.info(
                'mention-extension',
                `No user found with the name ${query}. We will not insert a mention node.`
              );
            });

           */ return commands.insertContentAt(range, {
                        type: this.name,
                        attrs: {
                            user: {
                                username: query
                            }
                        }
                    });
                }
        };
    },
    parseHTML () {
        return [
            {
                tag: `[data-da-type="${this.name}"]`
            }
        ];
    },
    renderHTML (props) {
        const { HTMLAttributes: { 'data-user': user } } = props, restHTMLAttributes = _object_without_properties(props.HTMLAttributes, [
            'data-user'
        ]);
        return [
            'span',
            mergeAttributes({
                class: s['anchor'],
                'data-da-type': this.name,
                'data-user': JSON.stringify(user)
            }, restHTMLAttributes),
            `${suggestionExtensionConfig.char}${user.username}`
        ];
    },
    renderText ({ node }) {
        return `${suggestionExtensionConfig.char}${node.attrs.user.username}`;
    },
    addKeyboardShortcuts () {
        return {
            Backspace: ()=>this.editor.commands.command(({ tr, state })=>{
                    let isMention = false;
                    const { selection } = state;
                    const { empty, anchor } = selection;
                    if (!empty) {
                        return false;
                    }
                    state.doc.nodesBetween(anchor - 1, anchor, (node, pos)=>{
                        if (node.type.name === this.name) {
                            isMention = true;
                            tr.insertText(this.options.deleteTriggerWithBackspace ? '' : suggestionExtensionConfig.char || '', pos, pos + node.nodeSize);
                            return false;
                        }
                    });
                    return isMention;
                })
        };
    },
    addProseMirrorPlugins () {
        return [
            Suggestion(_object_spread_props(_object_spread({
                editor: this.editor
            }, suggestionExtensionConfig), {
                items: getItems
            }))
        ];
    }
});

//# sourceMappingURL=mention.js.map